import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, CardNotification, Drawer, Heading, Loading, toast } from "@foris/avocado-suite";
import { useLayoutStore } from "@context/useLayoutStore";
import { IErrorWarning } from "@models/Errors/ErrorWarning";
import {
  CourseComponent,
  CubeMutation,
  EditSectionPayload,
  SectionValidationErrorUnion,
} from "@models/ISchema";
import { useMutation } from "@apollo/client";
import { MUTATION_EDIT_SECTION } from "@modules/sections/GridSections/ChangeComponent/graphql/changeComponent.queries";
import SearchComponent from "@modules/sections/GridSections/ChangeComponent/SearchComponent";
import Validations from "@common/components/Validations/Validations";
import SectionEdit from "@models/Errors/SectionEdit";
import useContextUrl from "@common/hooks/useContextUrl";
import styles from "./changeComponentDrawer.module.scss";

interface ChangeComponentDrawerProps {
  isOpen: boolean;
  component?: any;
  courseId?: any;
  sectionId?: any;
  courseComponent?: CourseComponent;
  onClose: () => void;
  onSuccess?: () => void;
}

const ChangeComponentDrawer = ({
  isOpen,
  component,
  courseId,
  sectionId,
  courseComponent,
  onClose,
  onSuccess,
}: ChangeComponentDrawerProps) => {
  const { scenario, origin } = useContextUrl();
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const [componentMutation] = useMutation(MUTATION_EDIT_SECTION, {});

  const initSelect = { label: component?.code, value: courseComponent?.id };
  const [selectValue, setSelectValue] = useState(initSelect);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [validationArray, setValidationArray] = useState<IErrorWarning[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [valueCheck, setValueCheck] = useState(null);

  useEffect(() => {
    setSelectValue(initSelect);
  }, [component?.code, courseComponent?.id]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const onSaveChangeComponent = async (commit: boolean) => {
    try {
      setIsLoading(true);

      const objMutation = {
        scenarioId: scenario,
        originId: origin,
        input: {
          sectionId: sectionId,
          dryRun: !commit,
          skipValidations: commit,
          changeset: {
            changeCourseComponent: {
              op: "CHANGE",
              courseComponentId: selectValue && selectValue.value,
            },
          },
        },
      };

      if (!commit) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const error = await callMutationHook(objMutation);
        error && setIsLoading(false);
        return;
      }

      const data = await componentMutation({ variables: objMutation });
      const dataMutation: EditSectionPayload = data.data.cube.editSection;
      setIsLoading(false);

      if (dataMutation.commited) {
        toast.success(t("calendar-view.success-alert.title"), {
          autoClose: 3000,
        });
        onSuccess?.();
        onClose?.();
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const callMutationHook = async (variables: any) => {
    try {
      const data = await componentMutation({ variables });
      const dataMutation: CubeMutation = data.data.cube;
      const validationErrors: Array<SectionValidationErrorUnion> =
        dataMutation.editSection.validationErrors;
      const errorsValidations = [];

      if (validationErrors && validationErrors.length > 0) {
        validationErrors.forEach((error: SectionValidationErrorUnion) => {
          const errorsComponent = new SectionEdit(error);
          errorsValidations.push(errorsComponent.getMessage());
        });
        setValidationArray(errorsValidations);
        return true;
      }

      onSaveChangeComponent(true);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const warnings = validationArray
    ? validationArray.filter(warning => warning.type === "WARNING")
    : [];

  return (
    <>
      {isLoading && <Loading />}
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        heightGap={visibleHeaderPixels}
        closeOnOverlayClick
        title={<Heading type="h3">{t("calendar-view.title")}</Heading>}
        footer={
          <div className={styles.drawerFooter}>
            <Button variant="ghost" onClick={() => onClose()}>
              {t("calendar-view.btn-cancel-and-back")}
            </Button>

            {validationArray && validationArray.length === 0 && (
              <Button
                disabled={!selectValue || isLoading || selectValue?.value === initSelect?.value}
                onClick={() => onSaveChangeComponent(false)}
              >
                {t("calendar-view.btn-save")}
              </Button>
            )}

            {warnings.length > 0 && (
              <Button
                disabled={!valueCheck || isLoading}
                onClick={() => onSaveChangeComponent(true)}
              >
                {t("calendar-view.btn-confirm")}
              </Button>
            )}
          </div>
        }
      >
        <section className={styles.changeComponentDrawer}>
          <SearchComponent
            initValue={selectValue}
            courseId={courseId}
            sendValues={setSelectValue}
            onDisabled={setShowNotification}
          />

          {showNotification && (
            <CardNotification
              outlined
              className={styles.notification}
              state="info"
              title={t("calendar-view.info-alert.title")}
            >
              {t("calendar-view.info-alert.description")}
            </CardNotification>
          )}

          <Validations
            validations={validationArray}
            onCallback={(valueCheck: boolean) => setValueCheck(valueCheck)}
          />
        </section>
      </Drawer>
    </>
  );
};

export default ChangeComponentDrawer;
